<template>
  <circle-loader v-if="loading" :inline="true" />
  <template v-else><m1-link ref="m1Widget" :config="config"></m1-link></template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { CreateWidgetRequest } from "@/models/api";
import type { Applicant } from "@/models/application";
import type { PropType } from "vue";
import { $api } from "@/services/api";
import {
  MeasureOneEvent,
  MeasureOneIndividualResponse,
  DataSourceConnectedEventDetail,
} from "@/models/widgets";
import { useStore } from "vuex";
import { allKeysHaveValues } from "@/use/validation";
import CircleLoader from "@/components/shared/CircleLoader.vue";

const store = useStore();

const props = defineProps({
  lenderId: {
    type: Number,
    required: true,
  },
  applicant: {
    type: Object as PropType<Applicant>,
    required: true,
  },
  stipulationId: {
    type: Number,
    required: true,
    default: 0,
  },
});

const emits = defineEmits<{
  (event: "datasourceConnected", value?: DataSourceConnectedEventDetail): void;
  (event: "exitRequested"): void;
  (event: "success"): void;
}>();

const measureOneEvents = [
  "exitRequested",
  "datasourceConnected",
  "sessionRejected",
  "individualUpdated",
  "currentProgress",
];

const loading = ref<boolean>(true);
const widgetVisible = ref<boolean>(false);
const hostName = ref<string>("api-stg.measureone.com");
const datarequestId = ref<string | null>(null);
const accessToken = ref<string>("");
const m1Widget = ref<HTMLElement | null>(null);
const measureOneEnabled = computed<boolean>(() => store.state.configuration?.measureOneEnabled);
const currentProgress = ref<number>(0);

const config = computed(() => {
  return {
    access_key: accessToken.value,
    host_name: hostName.value,
    datarequest_id: datarequestId.value,
    options: {
      display_profile: false,
      allow_close_widget: false,
    },
  };
});

const toggleWidget = (flag: boolean) => {
  widgetVisible.value = flag;
};

const request = computed(() => {
  return {
    applicationId: props.applicant.applicationId,
    applicantId: props.applicant.id,
    lenderId: props.lenderId,
    programTypeId: store.state?.appData?.application?.programTypeId,
    productTypeId: store.state?.appData?.application?.productTypeId,
    firstName: props.applicant.firstName,
    middleName: props.applicant.middleName,
    lastName: props.applicant.lastName,
    email: props.applicant?.contactInfo?.emailAddress,
    dateOfBirth: props.applicant.dateOfBirth,
    stipulationId: props.stipulationId,
  } as CreateWidgetRequest;
});

function setWidgetWidth() {
  // Overrides the widget's width
  const elements = m1Widget.value?.shadowRoot?.children;
  const elementsArray: HTMLElement[] = elements ? ([...elements] as HTMLElement[]) : [];
  const m1Component = elementsArray.find((e: HTMLElement) => e.classList.contains("m1-theme"));
  m1Component?.classList?.add("w-100");
}
async function fetchRequestDetails() {
  loading.value = true;

  await $api.widget
    .createMeasureOneWidget(request.value)
    .then((response: MeasureOneIndividualResponse) => {
      if (allKeysHaveValues<MeasureOneIndividualResponse>(response)) {
        datarequestId.value = response.dataRequestId;
        accessToken.value = response.accessToken;
        hostName.value = response.hostName;

        prepareConfig();
        setWidgetWidth();
        widgetVisible.value = true;
      } else {
        throw new Error("Widget Config Missing");
      }
    })
    .catch((error: any) => console.log("Error setting up MeasureOne widget: ", error))
    .finally(() => (loading.value = false));
}

const prepareConfig = () => {
  // Adds configuration
  m1Widget.value?.setAttribute("config", JSON.stringify(config.value));
  widgetVisible.value = true;
};

const handleExitRequested = (event: MeasureOneEvent) => {
  // Destroy the widget
  datarequestId.value = null;
  widgetVisible.value = false;

  emits("exitRequested");
};

const handleWidgetEvent = (event: any) => {
  console.log("MeasureOne Widget Event:", event.type, event.detail);
  // Add logic to handle different events like session.rejected, etc.
};

const handleDatasourceConnected = (event: MeasureOneEvent) => {
  // Update stip status to pending

  // Destroy the widget
  datarequestId.value = null;
  widgetVisible.value = false;

  emits("success");
  // Emit a custom event to the parent component
  emits("datasourceConnected", event.detail as DataSourceConnectedEventDetail);
};

function handleMeasureOneEvent(event: MeasureOneEvent) {
  console.log("handleMeasureOneEvent event", event);

  if (event.type === "datasourceConnected") {
    handleDatasourceConnected(event);
  } else if (event.detail.name === "exitRequested") {
    handleExitRequested(event);
  } else {
    return;
  }
}

async function openMeasureOneLinkWidget() {
  loading.value = true;
  if (datarequestId.value === null || datarequestId.value === undefined) {
    await fetchRequestDetails();
    await nextTick();
    createMeasureOneEvents();
    return;
  }

  loading.value = false;
}

const createMeasureOneEvents = () => {
  const el = m1Widget.value;
  if (el) {
    measureOneEvents.forEach(evt => {
      el.addEventListener(evt, e => handleMeasureOneEvent(e as unknown as MeasureOneEvent));
    });
  }
};

const removeMeasureOneEvents = () => {
  const el = m1Widget.value;
  if (!el) return;

  measureOneEvents.forEach(evt => {
    el.removeEventListener(evt, e => handleMeasureOneEvent(e as unknown as MeasureOneEvent));
  });
};

// 1. On component mount, fetch the data request ID from your back-end.
onMounted(async () => {
  await openMeasureOneLinkWidget();
  setWidgetWidth();
});

onBeforeUnmount(() => {
  removeMeasureOneEvents();
});

defineExpose({
  hostName,
  datarequestId,
  accessToken,
  config,
  m1Widget,
  toggleWidget,
  openMeasureOneLinkWidget,
  loading,
  currentProgress,
	measureOneEnabled,
});
</script>
